<template>
  <div ref="chartElement" class="base-chart" :style="{ width, height }"></div>
</template>

<script setup lang="ts">
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import * as echarts from 'echarts/core';
  import { BarChart, HeatmapChart, LineChart, PieChart } from 'echarts/charts';
  import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
    VisualMapComponent,
  } from 'echarts/components';
  import { CanvasRenderer } from 'echarts/renderers';
  import type { ECharts, EChartsCoreOption } from 'echarts/core';

  echarts.use([
    BarChart,
    LineChart,
    PieChart,
    HeatmapChart,
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
    VisualMapComponent,
    CanvasRenderer,
  ]);

  const props = withDefaults(
    defineProps<{
      option: EChartsCoreOption;
      height?: string;
      width?: string;
    }>(),
    { height: '320px', width: '100%' },
  );

  const chartElement = ref<HTMLDivElement | null>(null);
  let chart: ECharts | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const renderChart = async () => {
    await nextTick();
    if (!chartElement.value) return;
    if (!chart) chart = echarts.init(chartElement.value);
    chart.setOption(props.option, true);
  };

  watch(() => props.option, renderChart, { deep: true });

  onMounted(() => {
    renderChart();
    if (chartElement.value) {
      resizeObserver = new ResizeObserver(() => chart?.resize());
      resizeObserver.observe(chartElement.value);
    }
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    chart?.dispose();
    chart = null;
  });
</script>

<style scoped>
  .base-chart {
    min-width: 0;
  }
</style>
